import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';

import { cn } from 'helpers/classnames';
import Tooltip from 'components/common/Tooltip';
import FinalSale from 'components/common/FinalSale';
import { Loader } from 'components/Loader';
import { ProductImage } from 'components/cart/ProductImage';
import { withErrorBoundary } from 'components/common/MartyErrorBoundary';
import { RETURN_REASONS } from 'constants/returnReasons';
import { isLineItemReturnable } from 'helpers/MyAccountUtils';
import ReturnsUtils from 'helpers/ReturnsUtils';
import useMartyContext from 'hooks/useMartyContext';
import { ReturnStep } from 'constants/returnSteps';
import InfoOutlineSmallIcon from 'components/common/IconsV2/InfoOutlineSmallIcon';

import css from 'styles/containers/returns/returnProductCards.scss';

const REASON_KEY_FOR_DEFAULT_EMPTY_OPTION = -1;

const makeInfoDetail = (asin, color, width, inseam, finalSale, url, ariaCardId, title, size, dataMaskPiiElements) => (
  <div id={ariaCardId} className={cn(css.description, 'zappos:body-m')} data-cs-mask={dataMaskPiiElements}>
    <Link className="zappos:body-m-medium" to={url}>
      {title}
    </Link>
    <dl>
      <dt>Asin</dt>
      <dd>{asin}</dd>

      <dt>Color</dt>
      <dd>{color}</dd>

      {size && (
        <>
          <dt>Size</dt>
          <dd>{size}</dd>
        </>
      )}

      {width && (
        <>
          <dt>Width</dt>
          <dd>{width}</dd>
        </>
      )}

      {inseam && (
        <>
          <dt>Inseam</dt>
          <dd>{inseam}</dd>
        </>
      )}

      {finalSale && <FinalSale />}
    </dl>
  </div>
);

const makeReturnReasons = (unitId, boxNum, onSelectReturnReason, isRepetitionConflictError, reasonCode, itemsMissingReturnReason, testId) => {
  const isMissingReason = itemsMissingReturnReason.some(item => item.unitId === unitId);
  return (
    <div className={cn(css.reasonContainer, isMissingReason ? css.reasonError : '')}>
      <div className={css.reasonsLabelWrapper}>
        <label className="zappos:body-s" htmlFor="reasons">
          Why are you returning this?
          <span className={css.requiredStar}> *</span>
        </label>
        <Tooltip
          tooltipId="returnReasonTooltip"
          wrapperClassName={css.returnReasonTooltipWrapper}
          tooltipClassName={css.returnReasonTooltip}
          direction="right"
          content="Please select the reason that best applies to this item. Your answer will help us improve the returns experience."
        >
          <InfoOutlineSmallIcon size={16} />
        </Tooltip>
      </div>
      <select
        id="reasons"
        data-unitid={unitId}
        data-boxnum={boxNum}
        disabled={isRepetitionConflictError}
        onChange={onSelectReturnReason}
        value={reasonCode}
        data-test-id={testId('returnReason')}
      >
        {RETURN_REASONS.map(reason => {
          const optionKey = reason.reasonCode ?? REASON_KEY_FOR_DEFAULT_EMPTY_OPTION;
          return (
            <option key={optionKey} value={reason.reasonCode}>
              {reason.text}
            </option>
          );
        })}
      </select>
    </div>
  );
};

const makeBoxDropdown = (boxNum, itemUniqueId, onProductBoxNumSelect, title, size) => {
  const options = [];

  if (boxNum !== 0) {
    for (let i = 1; i < boxNum + 1; i++) {
      options.push(
        <option key={`putInBox${i}`} value={`${i}-${itemUniqueId}`}>
          Box {i}
        </option>
      );
    }
  }

  return (
    boxNum > 1 && (
      <div className={css.boxSelect}>
        <label className="zappos:body-m" htmlFor={`boxSelection-${itemUniqueId}`} aria-label={`Select which box to place ${title} size ${size} in`}>
          I will be placing this item in box
        </label>
        <select id={`boxSelection-${itemUniqueId}`} onChange={onProductBoxNumSelect}>
          {options}
        </select>
      </div>
    )
  );
};

// when items come from orders state
export const getDimensions = product => {
  const { asin, name, attributes } = product;
  const baseDimensions = { asin, title: name };

  const dimensions = attributes.reduce((obj, attribute) => {
    baseDimensions[attribute.key.toLowerCase()] = attribute.value;
    return obj;
  }, baseDimensions);

  return dimensions;
};

const makeHeading = (orderDate, orderId, step, boxNum, singleBox) => {
  // render what box each item is in
  if (step === ReturnStep.SELECT_LABEL || step === ReturnStep.DONE) {
    return <div className={cn(css.heading, 'zappos:body-m')}>You are returning {!singleBox ? boxNum : ''}</div>;
  }

  // render days remaining and order number
  if (step === ReturnStep.SELECT_RETURN_ITEMS) {
    const daysRemaining = orderDate && ReturnsUtils.getDaysRemaining(orderDate);
    const formattedDate = orderDate?.split(' at ')[0];

    return (
      <>
        <div className={cn(css.heading, 'zappos:body-m')}>
          <div>
            Days Remaining <span>{daysRemaining}</span>
          </div>
          <div>
            Order Date <span>{formattedDate}</span>
          </div>
          <div>
            Order Number <span>{orderId}</span>
          </div>
        </div>

        <div className={css.legend}>
          <p>Return</p>
          <p>Item(s)</p>
        </div>
      </>
    );
  }

  // don't render anything (select_box step or anything else)
  return null;
};

export const ReturnProductCards = (
  {
    items,
    returnItems,
    orderDate,
    step,
    orderId,
    boxNum,
    onReturnChecked,
    onProductBoxNumSelect,
    singleBox,
    phoneNumber,
    onSelectReturnReason,
    isPrivateLabel = false,
    isShipmentOptionsError,
    isRepetitionConflictError,
    useCustomerReturnableFlag,
    itemsMissingReturnReason = []
  },
  {
    marketplace: {
      dataMaskPiiElements,
      account: { multiBoxReturns }
    }
  }
) => {
  const { testId } = useMartyContext();
  if (!items) {
    return <Loader />;
  }

  return (
    <div className={css.container}>
      {!isPrivateLabel && makeHeading(orderDate, orderId, step, boxNum, singleBox)}

      {items.map(item => {
        const { image, product, itemUniqueId, unitId, reasonCode } = item;

        const returnable = isLineItemReturnable(item, useCustomerReturnableFlag);
        if (step === ReturnStep.SELECT_RETURN_ITEMS && !returnable) {
          return null;
        }

        const { asin, title, color, size, width, inseam, finalSale } = product ? getDimensions(product) : item;
        const url = image?.href || product.productUrl;

        const isChecked = returnItems?.find(returnItem => returnItem.itemUniqueId === itemUniqueId);
        const ariaCardId = `aria-returnProductCard-${itemUniqueId || unitId}`;

        const infoDetail = makeInfoDetail(asin, color, width, inseam, finalSale, url, ariaCardId, title, size, dataMaskPiiElements);
        const returnReasonsDropdown =
          step === ReturnStep.SELECT_LABEL
            ? makeReturnReasons(unitId, boxNum, onSelectReturnReason, isRepetitionConflictError, reasonCode, itemsMissingReturnReason, testId)
            : null;
        const boxesDropdown =
          step === ReturnStep.SELECT_BOX && multiBoxReturns ? makeBoxDropdown(boxNum, itemUniqueId, onProductBoxNumSelect, title, size) : null;

        return (
          <div key={itemUniqueId || unitId} className={css.item}>
            {step === ReturnStep.SELECT_RETURN_ITEMS && (
              <div>
                <input
                  id={`item-${itemUniqueId}`}
                  defaultChecked={isChecked}
                  className={css.check}
                  onChange={() => onReturnChecked({ item, orderId })}
                  type="checkbox"
                  aria-describedby={ariaCardId}
                />
                <label htmlFor={`item-${itemUniqueId}`} aria-label="return" />
              </div>
            )}

            <ProductImage
              tabIndex="-1"
              ariaHidden={true}
              showFixedQuantity={true}
              className={css.image}
              item={product || item}
              dataTe="TE_RETURNS_PRODUCT_CLICK"
              data-test-id={testId('returningItems')}
              data-cs-mask={dataMaskPiiElements}
            />

            {isPrivateLabel ? (
              <>
                {infoDetail}
                {returnReasonsDropdown}
              </>
            ) : (
              <div className={css.info}>
                {infoDetail}
                {boxesDropdown}
                {returnReasonsDropdown}
              </div>
            )}
          </div>
        );
      })}
      {isShipmentOptionsError && (
        <p className={cn(css.errorMessage, 'zappos:body-m')}>
          Sorry, but we were unable to complete your return. Please contact our customer loyalty team at <strong>{phoneNumber}</strong>
        </p>
      )}
      {isRepetitionConflictError && <p className={cn(css.infoMessage, 'zappos:body-m')}>This item(s) has already been submitted for return.</p>}
    </div>
  );
};

ReturnProductCards.contextTypes = {
  marketplace: PropTypes.object,
  testId: PropTypes.func,
  router: PropTypes.object.isRequired
};

ReturnProductCards.displayName = 'ReturnProductCards';

export default withErrorBoundary(ReturnProductCards.displayName, ReturnProductCards);
